import React from 'react'
import { Formik } from 'formik'
import * as Yup from 'yup'
import get from 'lodash/get'
import moment, { Moment } from 'moment-timezone'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import ListItemText from '@mui/material/ListItemText'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import FormHelperText from '@mui/material/FormHelperText'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import { useTenantTimezone } from '../shared/contexts'
import TenantDatePicker from '../shared/TenantDatePicker'
import { t } from '../../i18n'
import { ServiceTask, RecurrenceType } from './models'


interface ServiceTaskFormProps {
  serviceTask?: ServiceTask
  onSubmitForm: (values: any, callback: (success: boolean, errorData: any) => void) => void
}

const WEEKDAYS: { value: number; label: string }[] = [
  { value: 1, label: t('service_tasks.weekdays.monday') },
  { value: 2, label: t('service_tasks.weekdays.tuesday') },
  { value: 3, label: t('service_tasks.weekdays.wednesday') },
  { value: 4, label: t('service_tasks.weekdays.thursday') },
  { value: 5, label: t('service_tasks.weekdays.friday') },
  { value: 6, label: t('service_tasks.weekdays.saturday') },
  { value: 7, label: t('service_tasks.weekdays.sunday') },
]

const RECURRENCE_TYPE_OPTIONS: { value: RecurrenceType; label: string }[] = [
  { value: 'day', label: t('service_tasks.recurrence_types.day') },
  { value: 'week', label: t('service_tasks.recurrence_types.week') },
  { value: 'month', label: t('service_tasks.recurrence_types.month') },
  { value: 'year', label: t('service_tasks.recurrence_types.year') },
  { value: 'days_of_week', label: t('service_tasks.recurrence_types.days_of_week') },
  { value: 'perform_once', label: t('service_tasks.recurrence_types.perform_once') },
]

const ServiceTaskForm: React.FC<ServiceTaskFormProps> = ({
  serviceTask,
  onSubmitForm,
}) => {
  const tenantTz = useTenantTimezone()
  // Fallback default for when no serviceTask is passed — start_date set to
  // tenant's tomorrow rather than browser's tomorrow.
  if (!serviceTask) {
    serviceTask = {
      id: null,
      title: '',
      active: true,
      description: '',
      recurrence_rule: {
        id: null,
        recurrence_type: 'day' as RecurrenceType,
        interval: 1,
        days_of_week: [],
        start_date: moment.tz(tenantTz).add(1, 'days'),
      },
    }
  }

  const getValidationSchema = () =>
    Yup.object().shape({
      title: Yup.string().required(t('service_tasks.validations.title')),
      description: Yup.string(),
      recurrence_rule: Yup.object().shape({
        interval: Yup.number().when('recurrence_type', {
          is: (recurrenceType: string) => recurrenceType !== 'perform_once',
          then: (schema) => schema.integer().min(1, t('service_tasks.validations.interval')).required(t('service_tasks.validations.interval')),
        }),
        days_of_week: Yup.array().when('recurrence_type', {
          is: 'days_of_week',
          then: (schema) => schema.min(1, t('service_tasks.validations.days_of_week')),
        }),
      }),
    });

  return (
    <Formik
      initialValues={serviceTask}
      validationSchema={getValidationSchema()}
      validateOnChange={true}
      validateOnBlur={true}
      onSubmit={(values, { setStatus, setSubmitting }) => {
        const payload = { ...values }
        return onSubmitForm(payload, (success, errorData) => {
          let status = {}
          if (!success) {
            const fields = Object.getOwnPropertyNames(errorData.full_errors)
            for (let key in fields) {
              status[`${fields[key]}_error`] =
                errorData.full_errors[fields[key]].join(', ')
            }
          }
          setStatus(status)
          setSubmitting(false)
        })
      }}
    >
      {({
        values,
        errors,
        touched,
        status,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        setFieldValue,
        setFieldTouched,
        setStatus,
      }) => {
        const enhancedHandleChange = (e) => {
          let newStatus = Object.assign({}, status)
          newStatus[`${e.target.name}_error`] = ''
          setStatus(newStatus)
          handleChange(e)
        }

        const renderField = (label: string, name: string, type: string, placeholder: string, value: string | number | null = null, inverted = true, autoComplete = 'off') => {
          const inputValue = value !== null ? value : get(values, name)
          const errorsPresent = get(errors, name) && get(touched, name)
          return (
            <div className="form-group">
              <label>{label}</label>
              <input
                id={name}
                name={name}
                type={type}
                onBlur={handleBlur}
                value={inputValue === null ? '' : inputValue}
                placeholder={placeholder}
                autoComplete={autoComplete}
                onChange={enhancedHandleChange}
                className={`form-control ${errorsPresent ? 'is-invalid' : ''} ${inverted ? 'inverted' : ''}`}
                step="any"
              />
              {errorsPresent && (
                <div className="invalid-feedback">
                  {get(errors, name)}
                </div>
              )}
            </div>
          );
        };

        const renderTextArea = (label, name, placeholder) => {
          const inputValue = values[name]
          const errorsPresent = get(errors, name) && get(touched, name)

          return (
            <div className="form-group">
              <label>{label}</label>
              <textarea
                id={name}
                name={name}
                onBlur={handleBlur}
                value={inputValue === null ? '' : inputValue}
                placeholder={placeholder}
                onChange={enhancedHandleChange}
                className={`form-control inverted ${errorsPresent ? 'is-invalid' : ''}`}
                rows={3}
              />
              {errorsPresent && (
                <div className="invalid-feedback">
                  {get(errors, name)}
                </div>
              )}
            </div>
          )

        }

        const renderCheckbox = (label, name) => {
          const errorsPresent =
            (errors[name] && touched[name]) || (status && status[`${name}_error`])
          return (
            <fieldset className="form-group boolean mt-4">
              <div className="form-check">
                <input
                  id={name}
                  name={name}
                  type="checkbox"
                  checked={values[name]}
                  onChange={enhancedHandleChange}
                  className={`form-check-input boolean ${
                    errorsPresent && 'is-invalid'
                  }`}
                />
                <label className="form-check-label boolean" htmlFor={name}>
                  {label}
                </label>
                {errorsPresent && (
                  <div className="invalid-feedback">
                    {errors[name] || (status && status[`${name}_error`])}
                  </div>
                )}
              </div>
            </fieldset>
          )
        }

        const renderSelect = (label: string, name: string, options: { value: string; label: string }[], value: string) => {
          const errorsPresent = get(errors, name) && get(touched, name)
          const labelId = `${name}-label`
          return (
            <FormControl fullWidth className="form-group" error={!!errorsPresent}>
              <InputLabel id={labelId}>{label}</InputLabel>
              <Select
                labelId={labelId}
                id={name}
                name={name}
                label={label}
                value={value}
                onBlur={handleBlur}
                onChange={enhancedHandleChange}
              >
                {options.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <ListItemText primary={option.label} />
                  </MenuItem>
                ))}
              </Select>
              {errorsPresent && (
                <FormHelperText>{get(errors, name)}</FormHelperText>
              )}
            </FormControl>
          )
        }

        const renderDaysOfWeekCheckboxes = () => {
          const selectedDays: number[] = values.recurrence_rule.days_of_week || []
          const errorsPresent = get(errors, 'recurrence_rule.days_of_week') && get(touched, 'recurrence_rule.days_of_week')

          const toggleDay = (day: number) => {
            const newDays = selectedDays.includes(day)
              ? selectedDays.filter((selectedDay) => selectedDay !== day)
              : [...selectedDays, day]
            setFieldTouched('recurrence_rule.days_of_week', true, false)
            setFieldValue('recurrence_rule.days_of_week', newDays, true)
          }

          return (
            <fieldset className="form-group mt-4">
              <label>{t('service_tasks.days_of_week')}</label>
              <div className="d-flex flex-wrap">
                {WEEKDAYS.map((weekday) => (
                  <FormControlLabel
                    key={weekday.value}
                    className="me-3"
                    control={
                      <Checkbox
                        id={`days_of_week_${weekday.value}`}
                        checked={selectedDays.includes(weekday.value)}
                        onChange={() => toggleDay(weekday.value)}
                      />
                    }
                    label={weekday.label}
                  />
                ))}
              </div>
              {errorsPresent && (
                <FormHelperText error>{get(errors, 'recurrence_rule.days_of_week')}</FormHelperText>
              )}
            </fieldset>
          )
        }

        const renderDatepicker = (label, name, value) => {
          const handleTimeChange = (time: Moment | null) => {
            if (time) {
              setFieldValue(name, time.format('YYYY-MM-DD'))
            } else {
              setFieldValue(name, null)
            }
          }

          return (
            <TenantDatePicker
              className="mt-3 service-task-start-time"
              sx={{backgroundColor: 'white', borderRadius: '10px', borderStyle: 'none', borderWitdh: '0'}}
              label={label}
              value={value}
              onChange={handleTimeChange}
            />
          )
        }

        return (
          <form onSubmit={handleSubmit} className="form-robis">
            {renderField(t('service_tasks.title'), 'title', 'text', t('service_tasks.enter_title'))}
            {renderTextArea(t('service_tasks.description'), 'description', t('service_tasks.enter_description'))}
            {renderSelect(
              t('service_tasks.recurrence_type'),
              'recurrence_rule.recurrence_type',
              RECURRENCE_TYPE_OPTIONS,
              values.recurrence_rule.recurrence_type
            )}
            {!['perform_once', 'days_of_week'].includes(values.recurrence_rule.recurrence_type) && renderField(
              t('service_tasks.interval'),
              'recurrence_rule.interval',
              'number',
              '',
              values.recurrence_rule.interval
            )}
            {values.recurrence_rule.recurrence_type === 'days_of_week' && renderDaysOfWeekCheckboxes()}
            {renderDatepicker(
              t('service_tasks.start_date'),
              'recurrence_rule.start_date',
              values.recurrence_rule.start_date ? moment.tz(values.recurrence_rule.start_date, tenantTz) : null
            )}
            {renderCheckbox(t('service_tasks.active'), 'active')}

            <div className="form-actions d-flex justify-content-end">
              <a className="btn btn-secondary inverted me-2" href="/admin/service_tasks">
                {t('cancel')}
              </a>
              <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                {t('save')}
              </button>
            </div>
          </form>
        )
      }}
    </Formik>
  )
}

export default ServiceTaskForm
